package com.example.hukakmp.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.cameraCaptureAvailable
import com.example.hukakmp.identification.IdentifiedFish
import com.example.hukakmp.identification.fishIdentificationAvailable
import com.example.hukakmp.model.FotoSeleccionada
import com.example.hukakmp.viewmodel.FishIdentificationViewModel
import io.github.ismoy.imagepickerkmp.domain.extensions.loadBytes
import io.github.ismoy.imagepickerkmp.features.imagepicker.model.ImagePickerResult
import io.github.ismoy.imagepickerkmp.features.imagepicker.ui.rememberImagePickerKMP

@Composable
fun FishIdentificationScreen(
    uiState: FishIdentificationViewModel.UiState,
    onAnalyze: (FotoSeleccionada) -> Unit,
    onClear: () -> Unit,
    onRetry: () -> Unit,
    onCreateReport: (FotoSeleccionada, List<IdentifiedFish>) -> Unit,
    onBack: () -> Unit,
) {
    val imagePicker = rememberImagePickerKMP()
    var pickerError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(imagePicker.result) {
        when (val result = imagePicker.result) {
            is ImagePickerResult.Success -> {
                val photo = result.photos.firstOrNull()
                if (photo == null) {
                    pickerError = "No se seleccionó ninguna fotografía."
                } else {
                    val bytes = photo.loadBytes()
                    pickerError = when {
                        bytes.isEmpty() -> "No se pudo leer la fotografía."
                        bytes.size > MAX_IDENTIFICATION_PHOTO_BYTES ->
                            "La fotografía supera los 10 MB. Elegí una imagen más liviana."
                        else -> null
                    }

                    if (pickerError == null) {
                        onAnalyze(
                            FotoSeleccionada(
                                nombre = photo.fileName ?: "captura.jpg",
                                bytes = bytes,
                                contentType = photo.mimeType ?: "image/jpeg",
                            ),
                        )
                    }
                }
                imagePicker.reset()
            }

            is ImagePickerResult.Error -> {
                pickerError = result.exception.message ?: "No se pudo abrir el selector de imágenes."
                imagePicker.reset()
            }

            is ImagePickerResult.Dismissed -> imagePicker.reset()
            else -> Unit
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                navigationIcon = {
                    TextButton(onClick = onBack) {
                        Text("‹", color = MaterialTheme.colorScheme.onPrimary, fontSize = 30.sp)
                    }
                },
                title = {
                    Column {
                        Text("Identificar captura", fontWeight = FontWeight.Bold)
                        Text(
                            if (uiState.photosRemaining > 0) {
                                "${uiState.photosRemaining} identificación con IA disponible hoy"
                            } else {
                                "Identificación con IA usada por hoy"
                            },
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onPrimary.copy(alpha = 0.76f),
                        )
                    }
                },
                actions = {
                    HukaLogoMark(
                        modifier = Modifier.padding(end = 16.dp).size(32.dp),
                        tint = MaterialTheme.colorScheme.onPrimary,
                    )
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    "🐟",
                    fontSize = 56.sp,
                )
                Text(
                    "¿Qué pescaste?",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                )
                Text(
                    "Elegí una foto clara de la captura. Huka valida primero que la imagen corresponda a pesca y después intenta identificar la especie.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(top = 6.dp).fillMaxWidth(),
                )
            }

            Spacer(Modifier.height(18.dp))

            if (!fishIdentificationAvailable) {
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    color = MaterialTheme.colorScheme.surfaceVariant,
                ) {
                    Text(
                        "La identificación con IA todavía no está habilitada para esta plataforma KMP.",
                        modifier = Modifier.padding(16.dp),
                        textAlign = TextAlign.Center,
                    )
                }
                return@Column
            }

            if (uiState.photo == null) {
                IdentificationSelectorCard(
                    pickerLoading = imagePicker.result is ImagePickerResult.Loading,
                    onCamera = {
                        pickerError = null
                        imagePicker.launchCamera()
                    },
                    onGallery = {
                        pickerError = null
                        imagePicker.launchGallery(
                            allowMultiple = false,
                            selectionLimit = 1,
                        )
                    },
                )
            } else {
                SelectedPhotoCard(uiState.photo)

                Spacer(Modifier.height(14.dp))

                when {
                    uiState.isAnalyzing -> AnalysisLoadingCard()
                    uiState.error != null -> AnalysisErrorCard(
                        message = uiState.error,
                        onRetry = onRetry,
                    )
                    uiState.result != null -> IdentificationResultCard(
                        result = uiState.result,
                    )
                }

                Spacer(Modifier.height(14.dp))

                val result = uiState.result
                if (
                    result != null &&
                    result.isFishingImage &&
                    result.species.isNotEmpty()
                ) {
                    Button(
                        onClick = {
                            uiState.photo?.let { photo ->
                                onCreateReport(photo, result.species)
                            }
                        },
                        modifier = Modifier.fillMaxWidth().height(52.dp),
                        shape = RoundedCornerShape(15.dp),
                    ) {
                        Text(
                            if (result.species.size == 1) {
                                "Crear parte con esta captura"
                            } else {
                                "Crear parte con estas especies"
                            },
                            fontWeight = FontWeight.Bold,
                        )
                    }
                    Spacer(Modifier.height(8.dp))
                }

                OutlinedButton(
                    onClick = {
                        onClear()
                        pickerError = null
                    },
                    enabled = !uiState.isAnalyzing,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(15.dp),
                ) {
                    Text("Elegir otra foto")
                }
            }

            pickerError?.let {
                Spacer(Modifier.height(12.dp))
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    color = MaterialTheme.colorScheme.errorContainer,
                ) {
                    Text(
                        it,
                        modifier = Modifier.padding(14.dp),
                        color = MaterialTheme.colorScheme.onErrorContainer,
                    )
                }
            }

            Spacer(Modifier.height(24.dp))
            Text(
                "La identificación es orientativa. Verificá la especie antes de tomar decisiones sobre consumo, manipulación o normativa.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun IdentificationSelectorCard(
    pickerLoading: Boolean,
    onCamera: () -> Unit,
    onGallery: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.primaryContainer,
        tonalElevation = 1.dp,
    ) {
        Column(
            modifier = Modifier.padding(22.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text("Analizar una fotografía", fontWeight = FontWeight.Bold)
            Text(
                "En Web podés seleccionar una imagen; en un celular el navegador puede ofrecer también la cámara.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.78f),
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 6.dp),
            )

            Spacer(Modifier.height(18.dp))

            if (cameraCaptureAvailable) {
                Button(
                    onClick = onCamera,
                    enabled = !pickerLoading,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                ) {
                    Text("📷 Sacar foto")
                }
                Spacer(Modifier.height(8.dp))
            }

            OutlinedButton(
                onClick = onGallery,
                enabled = !pickerLoading,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(14.dp),
            ) {
                if (pickerLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    Text("  Abriendo…")
                } else {
                    Text("🖼️ Elegir foto")
                }
            }
        }
    }
}

@Composable
private fun SelectedPhotoCard(photo: FotoSeleccionada) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 1.dp,
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(52.dp)
                    .background(
                        MaterialTheme.colorScheme.secondaryContainer,
                        RoundedCornerShape(14.dp),
                    ),
                contentAlignment = Alignment.Center,
            ) {
                Text("📷", fontSize = 24.sp)
            }
            Column(modifier = Modifier.padding(start = 12.dp).weight(1f)) {
                Text(photo.nombre, fontWeight = FontWeight.Bold, maxLines = 1)
                Text(
                    "${photo.bytes.size / 1024} KB · ${photo.contentType}",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun AnalysisLoadingCard() {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.55f),
    ) {
        Row(
            modifier = Modifier.padding(18.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 3.dp)
            Column(modifier = Modifier.padding(start = 14.dp)) {
                Text("Analizando fotografía…", fontWeight = FontWeight.Bold)
                Text(
                    "Huka está buscando especies visibles.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun AnalysisErrorCard(
    message: String,
    onRetry: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.errorContainer,
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                "No se pudo analizar la foto",
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onErrorContainer,
            )
            Text(
                message,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onErrorContainer,
                modifier = Modifier.padding(top = 5.dp),
            )
            TextButton(onClick = onRetry) { Text("Reintentar") }
        }
    }
}

@Composable
private fun IdentificationResultCard(
    result: com.example.hukakmp.identification.FishIdentification,
) {
    val valid = result.isFishingImage && result.species.isNotEmpty()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = if (valid) {
            MaterialTheme.colorScheme.secondaryContainer
        } else {
            MaterialTheme.colorScheme.tertiaryContainer
        },
    ) {
        Column(modifier = Modifier.padding(18.dp)) {
            Text(
                if (valid) "✓ Identificación completada" else "⚠ Revisá esta fotografía",
                fontWeight = FontWeight.Bold,
                color = if (valid) {
                    MaterialTheme.colorScheme.onSecondaryContainer
                } else {
                    MaterialTheme.colorScheme.onTertiaryContainer
                },
            )

            Text(
                result.summary,
                style = MaterialTheme.typography.bodyMedium,
                modifier = Modifier.padding(top = 7.dp),
            )

            if (result.species.isNotEmpty()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                    verticalArrangement = Arrangement.spacedBy(9.dp),
                ) {
                    result.species.forEachIndexed { index, fish ->
                        Surface(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(14.dp),
                            color = MaterialTheme.colorScheme.surface.copy(alpha = 0.82f),
                        ) {
                            Column(modifier = Modifier.padding(13.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                ) {
                                    Text(
                                        "${index + 1}. ${fish.commonName}",
                                        fontWeight = FontWeight.Bold,
                                    )
                                    fish.confidence?.let {
                                        Text(
                                            "$it%",
                                            color = MaterialTheme.colorScheme.secondary,
                                            fontWeight = FontWeight.Bold,
                                        )
                                    }
                                }
                                fish.scientificName?.let {
                                    Text(
                                        it,
                                        style = MaterialTheme.typography.bodySmall,
                                        fontStyle = FontStyle.Italic,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private const val MAX_IDENTIFICATION_PHOTO_BYTES = 10 * 1024 * 1024
